/** Detect end-of-life .NET 6 runtimes from installed-software snapshot payloads. */

const DOTNET6_NAME_HINTS = [
  'microsoft .net runtime',
  'microsoft windows desktop runtime',
  'microsoft asp.net core',
  'microsoft .net sdk',
  'microsoft .net host',
  'microsoft .net target',
  'microsoft .net tool',
]

const DOTNET6_IN_NAME = /\b6\.\d/i

function normalisePrograms(payload) {
  if (!payload || typeof payload !== 'object') return []
  const programs = payload.programs
  if (!Array.isArray(programs)) return []
  return programs.filter((row) => row && typeof row === 'object')
}

function versionIsDotNet6(version) {
  const text = String(version ?? '').trim()
  return text === '6' || text.startsWith('6.')
}

function nameIsDotNet6Product(name) {
  const lower = String(name ?? '').trim().toLowerCase()
  if (!lower || lower.includes('.net framework')) return false
  if (DOTNET6_NAME_HINTS.some((hint) => lower.includes(hint))) return true
  return lower.includes('.net') && DOTNET6_IN_NAME.test(lower)
}

export function isDotNet6Program(name, version = null) {
  if (!nameIsDotNet6Product(name)) return false
  if (versionIsDotNet6(version)) return true
  return DOTNET6_IN_NAME.test(String(name ?? ''))
}

/**
 * @returns {{ hasData: boolean, installed: boolean, matches: Array<{ name, version, publisher, install_date }> }}
 */
export function detectDotNet6FromPayload(payload) {
  const programs = normalisePrograms(payload)
  const hasProgramsKey = payload && typeof payload === 'object' && 'programs' in payload
  const hasData = programs.length > 0 || hasProgramsKey

  if (!hasData) {
    return { hasData: false, installed: false, matches: [] }
  }

  const matches = []
  for (const row of programs) {
    const name = row.name ?? row.Name ?? null
    const version = row.version ?? row.Version ?? null
    if (!isDotNet6Program(name, version)) continue
    matches.push({
      name: name != null ? String(name).trim() : null,
      version: version != null ? String(version).trim() : null,
      publisher: row.publisher ?? row.Publisher ?? null,
      install_date: row.install_date ?? row.InstallDate ?? null,
    })
  }

  return {
    hasData: true,
    installed: matches.length > 0,
    matches,
  }
}

export function dotNet6MatchSummary(matches, maxItems = 3) {
  if (!matches?.length) return null
  const parts = matches.slice(0, maxItems).map((row) => {
    const name = (row.name || 'Unknown').trim()
    const version = (row.version || '').trim()
    return version ? `${name} (${version})` : name
  })
  let text = parts.join('; ')
  if (matches.length > maxItems) {
    text += ` (+${matches.length - maxItems} more)`
  }
  return text
}
